const enUS = {
  app: {
    title: 'Habit Garden',
  },
  install: {
    title: 'Add to home screen for quick access',
    action: 'Install',
    iosHint: 'Tap the Share button, then select "Add to Home Screen"',
  },
  common: {
    systemSettings: 'System Settings',
    logout: 'Log out',
    logoutShort: 'Log out',
    current: 'Current',
    close: 'Close',
    delete: 'Delete',
    save: 'Save',
    saveSettings: 'Save settings',
    cancel: 'Cancel',
    childrenCount: '{{count}} children',
    loading: 'Loading...',
    confirm: 'Confirm',
    prestigeReason: 'Prestige achieved! 🎉',
    add: 'Add',
    all: 'All',
    unassigned: 'Unassigned',
    networkError: 'Network is unavailable. Please check your connection and try again.',
    networkRestored: 'Network restored. You can continue now.',
  },
  petLife: {
    states: {
      normal: 'Healthy',
      hungry: 'A little hungry',
      weak: 'Feeling weak',
      dying: 'Needs help now',
      dead: 'Sleeping',
    },
    hints: {
      normal: 'Keep adding points to keep your pet energetic.',
      hungry: 'Add points once to cheer it up again.',
      weak: 'Your pet looks weak. Feed it with points soon.',
      dying: 'Your pet is in danger. Encourage it right away.',
      dead: 'Your pet is sleeping. Add points to wake it up.',
    },
  },
  settings: {
    tabs: {
      students: 'Children',
      items: 'Behaviors',
      groups: 'Groups',
      growth: 'Growth',
      parentShare: 'Share Link',
      account: 'Basics',
      checkin: 'Check-in Tasks',
    },
    parentShare: {
      title: 'Share Link',
      subtitle: 'Generate a link and share it. Family members can enter the child name to view records and the pet catalog.',
      enabled: 'Enabled',
      disabled: 'Disabled',
      disabledHint: 'When disabled, parents will see a notice that sharing is turned off.',
      processing: 'Processing...',
      generateLink: 'Generate link',
      copyLink: 'Copy link',
      rotateLink: 'Reset link',
      linkPlaceholder: 'The link will appear here after generating',
      tip: 'Tip: To invalidate old links, click "Reset link".',
      footerHint: 'Generate the link and share it to family members',
      toast: {
        generated: 'Link generated',
        generateFailed: 'Failed to generate link',
        rotated: 'Link reset',
        rotateFailed: 'Failed to reset link',
        copied: 'Link copied',
        copyFailed: 'Copy failed',
      },
    },
    account: {
      changePassword: 'Change password',
      currentClassName: 'Current family name',
      classList: 'Families',
      addClass: 'Add family',
      newClassPlaceholder: 'e.g. Smith Family',
      childrenCount: '{{count}} children',
      creating: 'Creating...',
      create: 'Create',
      deleteClass: 'Delete family',
      desktop: {
        title: 'Add to Desktop',
        desc: 'Add a shortcut to your desktop for quick access.',
        download: 'Download Shortcut',
        hint: 'Drag the downloaded file to your desktop',
        filename: 'FamilyPet',
      },
    },
    license: {
      permanent: 'Unlimited',
      validUntil: 'Valid until: {{date}}',
    },
    language: {
      label: 'Language',
      zhCN: '简体中文',
      zhTW: '繁體中文',
      enUS: 'English',
    },
    footer: {
      hintAccount: 'Click "Save" to apply changes.',
      hintGrowth: 'Click "Save" to apply changes.',
      hintInstant: 'Changes apply immediately on this page.',
    },
    icons: {
      honor: 'Honor',
      study: 'Study',
      activity: 'Activities',
      nature: 'Nature',
      food: 'Food',
      life: 'Life',
    },
    growth: {
      title: 'Growth stages',
      subtitle: 'Set the cumulative points required for each level',
      level: 'Lv.{{level}}',
      tip: 'Tip: Numbers represent total points earned. For example, if level 5 is 30, the child levels up to 5 after earning 30 points in total.',
      errorNotIncreasing: 'Level {{level}} points must be greater than the previous level',
    },
    sound: {
      title: 'Enable sound',
      subtitle: 'Control whether this family uses sound effects for points and level changes.',
    },
    petLife: {
      title: 'State rules',
      subtitle: 'Set how many days without points are needed before a pet enters each state.',
      enabled: 'Enable status decay',
      disabled: 'Disabled',
      enabledDesc: 'Pets will become hungry/weak based on days without points',
      disabledDesc: 'Pets will always stay healthy',
      hungryAfterDays: 'Hungry after',
      weakAfterDays: 'Weak after',
      dyingAfterDays: 'Dying after',
      sleepAfterDays: 'Sleep after',
      reset: 'Reset defaults',
      tip: "Thresholds must increase in order. The system uses natural days to count days without points. If a child's points are negative, the pet enters Sleeping immediately; only when points are back to 0 or above will the thresholds apply. Adding points restores the pet to normal immediately (if points are still negative, it stays Sleeping).",
      rulePreview: 'Current rule: hungry after {{hungryAfterDays}} days, weak after {{weakAfterDays}} days, dying after {{dyingAfterDays}} days, sleeping after {{sleepAfterDays}} days.',
      skipWeekends: 'Weekends (Sat & Sun) do not count toward decay days',
      validation: {
        order: 'Please enter valid increasing thresholds: hungry < weak < dying < sleep, and all values must be at least 1.',
      },
      preview: {
        title: 'Pet state legend',
        hungry: 'Hungry',
        weak: 'Weak',
        dying: 'Dying',
        dead: 'Sleeping',
        zoomHint: 'Click to zoom',
      },
    },
    items: {
      title: 'Behaviors',
      subtitle: 'Configure behavior items ({{count}})',
      hint: '(Hint: Max level requires {{maxPoints}} points. Adjust values accordingly.)',
      importSystemTemplate: 'Import system template',
      importSystemTemplateToast: 'System template imported: added {{created}}, skipped {{skipped}}',
      importSystemTemplateFailed: 'Failed to import system template',
      importSystemTemplateModal: {
        title: 'Import system template',
        desc: 'Append built-in check-in tasks, point items, and store goods to this family without overwriting existing entries.',
        confirm: 'Import',
        ageRangeLabel: 'Age range',
        'ageRange_3-5': 'Ages 3-5',
        'ageRange_6-9': 'Ages 6-9',
        ageRange_10plus: 'Ages 10+',
      },
      subTabs: {
        bonus: 'Add points',
        deduction: 'Deduct points',
      },
      newItemPlaceholder: {
        bonus: 'Item name (e.g. Helping with chores)',
        deduction: 'Item name (e.g. Skipping bedtime)',
      },
      points: '{{points}} pts',
    },
    students: {
      title: 'Children',
      subtitle: '{{count}} children in this family',
      bulkAdd: '📄 Bulk add',
      bulkCancel: 'Cancel bulk',
      bulkHint: 'One child name per line (max 50). You can paste from Excel:',
      bulkPlaceholder: 'Alice\nBob\nCharlie',
      addAll: 'Add all',
      singlePlaceholder: 'Enter child name...',
      bulkAddSuccess: 'Added {{count}} children',
      bulkAddFailed: 'Bulk add failed',
      alreadyExists: 'Child already exists',
      skipped: 'Added {{added}} children, skipped {{skipped}} duplicates',
      allSkipped: 'All children already exist, nothing to add',
      deleteConfirm: {
        title: 'Delete child?',
        descPrefix: 'Are you sure you want to delete ',
        descSuffix: '?',
        descLine2: 'This cannot be undone.',
        confirm: 'Delete',
      },
    },
    pets: {
      assign: {
        title: 'Assign pets',
        subtitle: 'Randomly assign new pets to all children',
        action: 'Assign',
      },
      assignConfirm: {
        title: 'Confirm pet assignment?',
        descLine1: 'Assign random pets to all children without pets?',
        descLine2: 'Children who already have pets will not be affected.',
        confirm: 'Confirm',
      },
      assignSuccess: 'Assigned',
      assignFailed: 'Assignment failed',
      reset: {
        title: 'Reset Family Progress',
        subtitle: 'Clear points, pets, badges, logs, and redemption records. Cannot be undone.',
        action: 'Reset Family Progress',
        cleared: 'Will be Cleared',
        kept: 'Will be Kept',
        clearedItems: {
          points: 'Points',
          petSelection: 'Pet Selection',
          petStage: 'Pet Stage',
          badges: 'Badges',
          coins: 'Coins',
          growthRecords: 'Growth Records',
          exchangeRecords: 'Exchange Records',
        },
        keptItems: {
          scoreRules: 'Add/Subtract Score Rules',
          levelRules: 'Level Rules',
          className: 'Family Name',
          groups: 'Groups',
          shopConfig: 'Shop Config',
        },
        warning: 'This operation cannot be undone. Please export CSV for backup if needed.',
        passwordLabel: 'Enter Password',
        passwordPlaceholder: 'Enter login password to confirm',
        confirm: 'Reset',
        success: 'Reset successful',
        failed: 'Reset failed',
      },
    },
    import: {
      fromOtherClass: 'Import from other family',
      success: 'Imported',
      failed: 'Import failed',
    },
    importModal: {
      title: 'Import settings',
      descPrefix: 'Select a source family. Its ',
      descSuffix: ' will ',
      override: 'override',
      descEnd: ' the current family settings.',
      selectSourceClass: 'Source family',
      selectClassPlaceholder: 'Select a family...',
      confirm: 'Import',
    },
    save: {
      success: 'Saved',
      failed: 'Save failed',
    },
    update: {
      success: 'Updated',
      failed: 'Update failed',
    },
    delete: {
      success: 'Deleted',
      failed: 'Delete failed',
    },
    class: {
      createSuccess: 'Family created',
      createFailed: 'Create failed',
      deleteSuccess: 'Family deleted',
      deleteFailed: 'Delete failed. Please try again later.',
      deleteFailedHttp: 'Delete failed (HTTP {{status}})',
      deleteNetworkError: 'Network error: {{message}}',
      deleteConfirm: {
        title: 'Delete family?',
        descPrefix: 'Are you sure you want to delete ',
        descSuffix: '?',
        descLine2: 'This family will be hidden and requires your password.',
        passwordLabel: 'Password',
        passwordPlaceholder: 'Enter password',
        passwordError: 'Incorrect password, please try again',
        deleting: 'Deleting...',
        confirm: 'Delete',
      },
    },
    iconPicker: {
      title: 'Choose icon',
    },
    password: {
      title: 'Change password',
      confirm: 'Confirm',
      success: 'Password changed successfully',
      error: {
        minLength: 'Password must be at least 6 characters',
        mismatch: 'Passwords do not match',
        failed: 'Change failed. Please check your current password.',
      },
      currentPassword: {
        label: 'Current password',
        placeholder: 'Enter current password',
        hint: 'Enter your current password',
      },
      newPassword: {
        label: 'New password',
        placeholder: 'Set a new password',
        hint: 'At least 6 characters',
      },
      confirmPassword: {
        label: 'Confirm new password',
        placeholder: 'Enter the new password again',
      },
    },
    groups: {
      title: 'Groups',
      subtitle: '{{count}} groups (max 50)',
      create: 'New Group',
      placeholder: 'Group name (1-20 chars)',
      add: 'Add',
      list: 'Group List',
      members: 'Group Members',
      manageMembers: 'Manage Members',
      removeMember: 'Remove from Group',
      random: {
        title: 'Random Grouping',
        studentCount: 'Total children: {{total}}. Randomly divide children into',
        label: 'groups',
        action: 'Randomize',
        confirmTitle: 'Confirm Randomize',
        confirmDesc: 'This will clear existing groups and re-assign into {{count}} groups. Continue?',
        confirmAction: 'Continue',
      },
      addStudent: {
        title: 'Add children to "{{groupName}}"',
        placeholder: 'Search child name',
        onlyUnassigned: 'Unassigned only',
        allowMultiple: 'Allow children in multiple groups',
        selected: '{{selected}}/{{total}} selected',
        selectAll: 'Select All',
        clear: 'Clear',
        confirm: 'Add {{count}} children',
        collapse: 'Collapse',
      },
      deleteConfirm: {
        title: 'Delete group?',
        descPrefix: 'Are you sure you want to delete ',
        descSuffix: '?',
        desc: 'Children in this group will become unassigned.',
        confirm: 'Delete',
      },
      edit: 'Edit Group',
    },
  },
  rank: {
    title: 'Leaderboard',
    selectClass: 'Family',
    boardTitle: '{{className}} Points Leaderboard',
    topHint: 'Top 20 children',
    studentNumber: 'ID',
    points: 'Points',
    empty: 'No ranking data',
  },
  petCenter: {
    title: 'Pet Center',
    currentClass: 'Current family',
    promptPetName: 'Name your habit pet:',
    exp: 'EXP',
    mood: 'Mood',
    hunger: 'Hunger',
    guardianPet: 'Family guardian pet',
    noPet: 'This family has not adopted a pet yet.',
    adoptNow: 'Adopt now',
    interactions: {
      title: 'Care & interact',
      feed: {
        title: 'Feed',
        desc: 'Costs 10 points and increases hunger.',
      },
      pet: {
        title: 'Pet',
        desc: 'Increases mood (coming soon).',
      },
      play: {
        title: 'Play',
        desc: 'Costs energy and increases EXP (coming soon).',
      },
    },
    log: {
      title: 'Growth Log',
      line1: '{{name}} just ate and looks happy!',
      line2: 'Congrats! {{name}} reached Lv.{{level}}.',
    },
  },
  classSelection: {
    title: {
      create: 'Create family',
      edit: 'Edit family',
      manage: 'Families',
    },
    form: {
      className: 'Family name',
      placeholder: 'e.g. Smith Family',
      confirmCreate: 'Create',
      saveChanges: 'Save',
    },
    footer: {
      createNew: 'Create a new family',
    },
  },
  leaderboard: {
    title: 'Leaderboard',
    subtitle: 'Raise pets and reach the top',
    badges: '{{count}} badges',
    points: '{{count}} points',
    total: 'Total',
    empty: 'No data yet',
    rule: '* Rule: 1 badge = {{badgePointValue}} pts + current points',
  },
  petSelection: {
    title: 'Choose a pet',
    subtitle: 'Choose a lovely pet for {{studentName}}',
    viewBaby: 'View baby form',
    viewMax: 'View max form',
    unlocked: 'Unlocked',
    hint: 'Click a pet to select. Your pet will accompany you on daily check-ins.',
  },
  pets: {
    unknown: 'Unknown pet',
    hamster: 'Hamster',
    hedgehog: 'Hedgehog',
    unicorn: 'Unicorn',
    phoenix: 'Phoenix',
    dog: 'Puppy',
    dolphin: 'Dolphin',
    fox: 'Fox',
    dinosaur: 'Dinosaur',
    dragon: 'Dragon',
    cat: 'Kitten',
    owl: 'Owl',
    raccoon: 'Raccoon',
    rabbit: 'Bunny',
    deer: 'Deer',
    panda: 'Panda',
    tiger: 'Tiger',
    elephant: 'Elephant',
    monkey: 'Monkey',
    penguin: 'Penguin',
    gorilla: 'Gorilla',
    butterfly: 'Butterfly',
    chicken: 'Chick',
    koala: 'Koala',
    lion: 'Lion',
    turtle: 'Turtle',
    sheep: 'Sheep',
    bee: 'Bee',
    treant: 'Treant',
    jellyfish: 'Jellyfish',
    robot: 'Robot',
    ultra: 'Gundam',
    polar_bear: 'Polar Bear',
    bat: 'Bat',
    chameleon: 'Chameleon',
    kangaroo: 'Kangaroo',
    crocodile: 'Crocodile',
    seahorse: 'Seahorse',
    walrus: 'Walrus',
    hippo: 'Hippo',
    flamingo: 'Flamingo',
    whale: 'Whale',
    peacock: 'Peacock',
    cow: 'Cow',
    frog: 'Frog',
    shark: 'Shark',
    squirrel: 'Squirrel',
    rhino: 'Rhino',
    pig: 'Pig',
    duck: 'Duck',
    parrot: 'Parrot',
    giraffe: 'Giraffe',
  },
  renamePet: {
    title: 'Rename Pet',
    subtitle: '{{studentName}} · {{petName}}',
    label: 'Pet Nickname',
    placeholder: 'Enter nickname',
    hint: 'Supports 1-12 characters (letters/numbers)',
    cancel: 'Cancel',
    save: 'Save',
  },
  resetPassword: {
    title: 'Reset password',
    subtitle: 'Verify with your activation code, then set a new password.',
    username: {
      label: 'Username',
      placeholder: 'Enter username',
    },
    code: {
      label: 'Activation code',
      placeholder: 'e.g. ABCD-YYYY-ZZZZ',
      hint: 'Use the activation code you registered with',
    },
    newPassword: {
      label: 'New password',
      placeholder: 'At least 6 characters',
      hint: 'Set a new password (at least 6 characters)',
    },
    confirmPassword: {
      label: 'Confirm password',
      placeholder: 'Enter the new password again',
    },
    processing: 'Processing...',
    submit: 'Reset password',
    success: 'Password reset successful. Please log in with your new password.',
    error: {
      minLength: 'Password must be at least 6 characters',
      mismatch: 'Passwords do not match',
      failed: 'Reset failed. Please check username and activation code.',
      foundUsername: 'Reset failed. This activation code belongs to: {{username}}.',
    },
  },
  growthRecords: {
    title: 'Growth records',
    count: '{{shown}} / {{total}} records',
    exportCsv: 'Export CSV',
    exportFilename: 'GrowthRecords_{{dateStr}}.csv',
    exportFailed: 'Export failed',
    undo: 'Undo',
    undoFailed: 'Undo failed',
    empty: 'No records',
    reverted: '(reverted)',
    unknownStudent: 'Unknown',
    csv: {
      time: 'Time',
      student: 'Child',
      action: 'Action',
      change: 'Change',
    },
    confirmUndo: {
      title: 'Confirm undo',
      defaultStudent: 'this child',
      add: 'add points',
      deduct: 'deduct points',
      desc: "This will undo {{name}}'s {{action}} record and create a revert record.",
      confirm: 'Confirm',
    },
    pagination: 'Page {{current}} / {{totalPages}} · {{totalItems}} records',
    tabs: {
      stats: 'Statistics',
      history: 'History',
    },
    stats: {
      week: 'This Week',
      month: 'This Month',
      lastMonth: 'Last Month',
      custom: 'Custom',
      query: 'Query',
      period: '{{start}} ~ {{end}}',
      totalGained: 'Total Gained',
      totalLost: 'Total Lost',
      netChange: 'Net Change',
      totalActions: 'Actions',
      activeStudents: 'Active Children',
      studentRanking: 'Children Ranking',
      rank: 'Rank',
      student: 'Child',
      gained: 'Gained',
      lost: 'Lost',
      net: 'Net',
      actions: 'Actions',
      dailyTrend: 'Daily Trend',
      noData: 'No data for this period',
      exportFilename: 'Stats_{{period}}_{{dateStr}}.csv',
    },
  },
  studentAction: {
    batchMode: 'Batch mode',
    batchTitle: 'Score {{count}} children',
    pointsUnit: 'pts',
    availablePointsUnit: 'avail',
    petsCountUnit: 'pets',
    currentExp: 'Current EXP',
    changePet: 'Change pet',
    tabs: {
      action: 'Score',
      calendar: 'Calendar',
      history: 'History',
      pets: 'Pets',
    },
    calendar: {
      today: 'Today',
      summary: '{{count}} check-ins this month · {{points}} pts',
      empty: 'No check-ins this month',
    },
    searchPlaceholder: 'Search point items...',
    checkinItems: 'Check-in',
    noCheckinTasks: 'No check-in tasks yet',
    positiveItems: 'Add points',
    negativeItems: 'Deduct points',
    behaviorItems: 'Behaviors',
    behaviorFilterAll: 'All',
    behaviorFilterPositive: 'Positive',
    behaviorFilterNegative: 'Negative',
    noItemsFound: 'No matching items',
    custom: {
      label: 'Custom',
      titlePositive: 'Custom Bonus',
      titleNegative: 'Custom Deduction',
      points: 'Points',
      pointsPlaceholder: 'Enter points',
      reason: 'Reason',
      optional: '(optional)',
      defaultPositive: 'Custom bonus',
      defaultNegative: 'Custom deduction',
    },
    noHistory: 'No scoring records',
    unknownAction: 'Unknown action',
    reverted: '(reverted)',
    undo: 'Undo',
    locked: 'Locked',
    undoFailed: 'Undo failed',
    export: 'Export History',
    confirmUndo: {
      title: 'Confirm undo',
      add: 'add points',
      deduct: 'deduct points',
      desc: 'This will undo this {{action}} record and create a revert record.',
      confirm: 'Confirm',
    },
    petStatus: {
      current: 'Current state',
      daysWithoutFeed: 'Days without points',
      daysCount: '{{count}} day(s)',
      nextState: 'Until next state',
      toSleep: 'Until sleep',
      remainingDays: '{{count}} day(s)',
      awakenByPoints: 'Add points to wake up',
      sleepHint: 'Your pet is waiting for new encouragement to recover.',
      none: '—',
    },
  },
  store: {
    title: 'Store',
    selectClass: 'Select family',
    addGood: 'Add item',
    stock: 'Stock: {{count}}',
    exchangeToStudent: 'Exchange to child',
    soldOut: 'Sold out',
    empty: 'No items yet. Add some!',
    alert: {
      selectClassFirst: 'Please select a family first',
      exchangeSuccess: 'Exchange successful!',
      exchangeFailed: 'Exchange failed',
    },
    addModal: {
      title: 'Add item',
      name: 'Item name',
      price: 'Price (points)',
      stock: 'Stock',
      confirm: 'Add',
    },
    exchangeModal: {
      title: 'Exchange item',
      desc: 'Exchanging: {{name}} (costs {{price}} points)',
      selectClass: 'Family',
      chooseClass: 'Select a family',
      selectStudent: 'Child',
      chooseStudent: 'Select a child',
      insufficient: '(insufficient points)',
      studentOption: '{{name}} (points: {{points}}) {{insufficient}}',
      confirm: 'Confirm',
    },
  },
  storeModal: {
    header: {
      storeTitle: 'Store',
      manageTitle: 'Manage store',
      tips: 'Every time you fully raise a pet, the system will reward you with 10 coins and 1 badge!',
    },
    tabs: {
      list: 'Items',
      history: 'History',
      manageLong: 'Manage items',
      manageShort: 'Manage',
    },
    emptyList: 'No items available',
    emptyListAction: 'Add items',
    noDescription: 'No description',
    redeemNow: 'Redeem',
    soldOut: 'Sold out',
    currentClass: 'Current family: {{name}}',
    noClassSelected: 'No family selected',
    importFromOther: 'Import from other family',
    stock: 'Stock',
    edit: 'Edit',
    addNew: 'Add new item',
    noExchangeRecords: 'No exchange records',
    record: {
      defaultAvatar: 'C',
      unknownStudent: 'Unknown child',
      redeemed: 'redeemed',
      unknownGood: 'Unknown item',
    },
    exchangeSelect: {
      title: 'Select a child',
      needCoins: 'Costs {{price}} points',
      searchPlaceholder: 'Search children...',
      currentCoins: 'Has {{coins}} available points',
      insufficientCoins: 'Insufficient available points',
      clickRedeem: 'Redeem',
      noStudentsFound: 'No matching children',
    },
    confirmRedeem: {
      title: 'Confirm redeem',
      desc: 'Redeem "{{goodName}}" for {{name}}?',
      cost: 'Will cost {{price}} available points',
    },
    confirmDelete: {
      title: 'Delete item?',
      desc: 'Are you sure you want to delete "{{name}}"? This cannot be undone.',
      deleting: 'Deleting...',
      confirm: 'Delete',
    },
    editModal: {
      editTitle: 'Edit item',
      addTitle: 'Add item',
      name: 'Item name',
      namePlaceholder: 'e.g. Extra screen time',
      description: 'Description',
      descriptionPlaceholder: 'e.g. 30 min extra screen time',
      price: 'Price (available points)',
      initialStock: 'Initial stock',
      iconType: 'Icon type',
    },
    importModal: {
      title: 'Import items',
      subtitle: 'Sync items from another family to the current family',
      sourceClass: 'Source family',
      required: 'Required',
      sourceClassPlaceholder: 'Select a source family (required)',
      searchPlaceholder: 'Search items...',
      selectAll: 'Select all',
      clear: 'Clear',
      selectedCount: 'Selected {{selected}} / {{total}}',
      selectSourceClassFirst: 'Select a source family',
      noImportableGoods: 'No items to import',
      importing: 'Importing...',
      startImport: 'Start import',
    },
    toast: {
      outOfStock: 'Out of stock',
      insufficientCoins: 'Insufficient available points',
      redeemSuccess: 'Redeemed! {{name}} got "{{goodName}}"',
      redeemFailed: 'Redeem failed',
      deleteSuccess: 'Deleted',
      deleteFailed: 'Delete failed',
      enterName: 'Please enter item name',
      enterPrice: 'Please enter item price',
      enterStock: 'Please enter initial stock',
      selectClassFirst: 'Please select a family first',
      fetchClassesFailed: 'Failed to fetch family list',
      fetchSourceGoodsFailed: 'Failed to fetch source items',
      selectSourceClass: 'Please select a source family',
      selectAtLeastOneGood: 'Select at least one item',
      importDone: 'Import done: created {{created}}, updated {{updated}}, skipped {{skipped}}',
      importFailed: 'Import failed',
    },
  },
  classManagement: {
    title: 'Family management',
    create: {
      button: 'New family',
      title: 'Create family',
      placeholder: 'Enter family name',
      confirm: 'Create',
    },
    edit: {
      title: 'Edit family',
      placeholder: 'Family name',
      save: 'Save',
    },
    delete: {
      title: 'Delete family',
      willDelete: 'About to delete: {{name}}',
      desc: 'This will hide the family from your system and requires your login password.',
      passwordLabel: 'Enter login password',
      passwordPlaceholder: 'Password',
      confirm: 'Delete',
      errorFallback: 'Delete failed. Please try again later.',
    },
    sidebar: {
      title: 'My families',
      empty: 'No families yet. Create one first.',
    },
    studentsTotal: 'Children: {{count}}',
    addStudent: {
      button: 'Add child',
      name: 'Name',
      studentNumber: 'Child ID (optional)',
    },
    table: {
      name: 'Name',
      studentNumber: 'ID',
      points: 'Points',
      actions: 'Actions',
      edit: 'Edit',
      empty: 'No children',
    },
    noClassSelected: 'Select a family on the left to view details',
  },
  login: {
    appName: 'Habit Garden',
    tagline1: 'A little progress every day, your pet grows a little too.',
    tagline2: 'Complete tasks, earn points, and unlock new companions.',
    tabs: {
      login: 'Log in',
      register: 'Register',
    },
    username: {
      label: 'Username',
      placeholder: 'Enter username',
    },
    password: {
      label: 'Password',
      placeholder: 'Enter password',
      show: 'Show password',
      hide: 'Hide password',
    },
    activationCode: {
      label: 'Activation code',
      placeholder: 'Enter activation code',
      hint: 'Provided by the administrator to verify your identity',
    },
    renewCode: {
      label: 'New activation code (renew)',
      placeholder: 'Enter new activation code',
      hint: 'You will be logged in automatically after renewal',
    },
    processing: 'Processing...',
    submit: {
      login: 'Log in',
      register: 'Register',
      renewAndLogin: 'Renew & log in',
    },
    forgotPassword: 'Forgot password? Reset with activation code ➜',
    switch: {
      noAccount: "Don't have an account?",
      hasAccount: 'Already have an account?',
      toRegister: 'Register',
      toLogin: 'Log in',
    },
    errors: {
      expired: 'Account expired. Enter a new activation code to renew and log in.',
      failed: 'Login failed. Please try again.',
    },
    rememberMe: 'Remember username',
  },
  home: {
    selectClass: 'Select family',
    searchPlaceholder: 'Search children...',
    nav: {
      store: 'Store',
      leaderboard: 'Leaderboard',
      reports: 'Reports',
      settings: 'Settings',
      tools: 'Tools',
    },
    tools: {
      title: 'Tools',
      random: {
        title: 'Random Picker',
        action: 'Start',
        rolling: 'Rolling...',
        reset: 'Reset',
        result: 'Selected: {{name}}',
        count: 'Count',
        review: 'Review',
        excludePicked: 'Exclude picked',
        remaining: '{{count}} remaining',
        resetPicked: 'Reset',
      },
      timer: {
        title: 'Timer / Stopwatch',
        countdown: 'Countdown',
        stopwatch: 'Stopwatch',
        start: 'Start',
        pause: 'Pause',
        reset: 'Reset',
        minutes: 'min',
        seconds: 'sec',
        timeUp: 'Time is up!',
        custom: 'Custom',
        set: 'Set',
        placeholder: 'Minutes (e.g. 0.5) or MM:SS',
      },
      noise: {
        title: 'Noise Detector',
        sensitivity: 'Sensitivity',
        level: 'Current Level',
        hint: 'Please allow microphone access to detect noise',
      },
    },
    sort: {
      title: 'Sort by',
      score: 'By score',
      name: 'By name',
    },
    viewMode: {
      compact: 'Compact view',
      normal: 'Normal view',
    },
    lock: {
      button: 'Lock',
      locked: 'Locked',
      unlockButton: 'Unlock',
      setTitle: 'Set Password',
      confirmTitle: 'Confirm Password',
      unlockTitle: 'Enter Password',
      setHint: 'Enter a 4-digit password',
      confirmHint: 'Enter the same password again',
      unlockHint: 'Enter lock password to unlock',
      cancel: 'Cancel',
      wrong: 'Wrong password, try again',
      mismatch: 'Passwords do not match, reset again',
      saveSuccess: 'Lock password saved',
      saveFailed: 'Failed to save lock password',
      changePassword: 'Change password',
      verifyTitle: 'Verify password',
      verifyHint: 'Enter current lock password',
      changeSuccess: 'Lock password changed',
    },
    batch: {
      enter: 'Batch',
      exit: 'Exit',
      hint: 'Tap cards to select children, then batch feed',
      selectedPrefix: 'Selected',
      selectedSuffix: 'children',
      selectAll: 'Select all',
      deselectAll: 'Deselect all',
      clear: 'Clear',
      feed: 'Batch feed',
    },
    empty: {
      noClasses: {
        title: 'No families yet',
        desc: 'Create a family to get started. You can add children, manage points, and pets.',
        action: 'Create family',
      },
      noStudents: {
        title: 'No children yet',
        desc: 'Add children to start the habit pet journey!',
        action: 'Add children',
      },
    },
    student: {
      adopt: 'Adopt',
      mysteryEgg: 'Mystery egg',
      progress: 'Progress',
      hatching: 'Hatching',
      maxLevel: 'MAX',
      remaining: '{{count}} left',
      changePet: 'Change',
      daysWithoutFeed: 'No points for',
      daysCount: '{{count}} day(s)',
      nextStateIn: '{{count}} day(s) to next state',
      sleepIn: '{{count}} day(s) to sleep',
      sleepHint: 'Your pet is sleeping. Add points to wake it up.',
      waitingWakeUp: 'Waiting for new encouragement',
    },
    summon: 'Summon Pet',
    feedback: {
      batchSuccess: 'Batch update success',
      addSuccess: 'Added',
      deductSuccess: 'Deducted',
      appliedTo: 'Applied to {{count}} children',
    },
    levelUp: {
      maxTitle: 'Max level!',
      levelUpTitle: 'Level up!',
      promote10: 'Promoted to Lv.10',
      maxAchieved: '⭐ Max achieved ⭐',
      promoteN: 'Promoted to Lv.{{level}}',
      maxForm: 'reached ultimate form',
      stronger: 'became stronger',
      footer: "{{name}}'s pet {{status}}",
    },
    prestige: {
      title: 'Prestige achieved!',
      congrats: 'Congrats {{name}}',
      petBadge: '⭐ Guardian pet · Max form ⭐',
      badgeReward: 'Prestige badge +1',
      coinsReward: 'Coins +10',
      note: '✨ Badges are saved permanently ✨',
      later: 'Later',
      collect: 'Collect badge',
    },
  },
  parentShare: {
    title: 'Family view',
    titleWithClass: '{{className}} · Family view',
    loading: 'Loading...',
    invalidLink: 'Invalid or expired link',
    disabledMessage: 'Sharing is turned off. Please contact the parent to enable it.',
    points: 'Points: {{points}}',
    nameLabel: 'Child name',
    namePlaceholder: 'Enter child name',
    nameHint: 'The name must match the parent\'s children list exactly (including any numbers/symbols). For example, if it is "01 LiSi", enter "01 LiSi".',
    submit: 'View',
    searching: 'Searching...',
    lookupFailed: 'Lookup failed. Please try again later.',
    currentExp: 'Current EXP',
    tabs: {
      history: 'Records',
      pets: 'Pets',
    },
    emptyHistory: 'No records',
    revertedSuffix: ' · Reverted',
    catalogTitle: 'Pet catalog',
    ownedCount: 'Owned: {{count}}',
    owned: 'Owned',
    locked: 'Locked',
    noPet: 'No pet',
    footerNote: 'This page is for viewing pet info only',
  },
  checkin: {
    title: 'Daily Check-in',
    todayProgress: 'Today {{done}}/{{total}}',
    complete: 'Complete',
    completed: 'Completed',
    undo: 'Undo',
    taskManagement: 'Check-in Task Management',
    taskManagementSubtitle: 'Configure daily check-in tasks ({{count}})',
    addTask: 'Add Task',
    weekdayShort: {
      mon: 'Mon',
      tue: 'Tue',
      wed: 'Wed',
      thu: 'Thu',
      fri: 'Fri',
      sat: 'Sat',
      sun: 'Sun',
    },
    form: {
      addTitle: 'New Check-in Task',
      editTitle: 'Edit Check-in Task',
      name: 'Task Name',
      points: 'Reward Points',
      dateRange: 'Date Range',
      dateHint: 'Leave empty for no start/end limit',
      dateNoLimit: 'Always active',
      dateRangeInvalid: 'Start date cannot be later than end date',
      clearDates: 'Clear',
      repeat: 'Repeats On',
      repeatRequired: 'Pick at least one day',
      preset: {
        all: 'All',
        weekdays: 'Weekdays',
        weekend: 'Weekend',
      },
    },
    taskName: 'Task Name',
    taskNamePlaceholder: 'e.g., Brush teeth, Read 30 min',
    taskPoints: 'Reward Points',
    noTasks: 'No check-in tasks yet. Add some in Settings.',
    noChildren: 'Please add children first',
    history: 'Check-in History',
    deleteTitle: 'Delete Check-in Task',
    deleteConfirm: 'Are you sure to delete task "{{name}}"? ',
    toast: {
      completeSuccess: 'Check-in completed!',
      completeFailed: 'Check-in failed',
      uncompleteSuccess: 'Check-in undone',
      uncompleteFailed: 'Undo failed',
      taskCreated: 'Task created',
      taskCreateFailed: 'Failed to create task',
      taskDeleted: 'Task deleted',
      taskDeleteFailed: 'Failed to delete task',
    },
  },
  onboarding: {
    title: 'Welcome to Habit Garden',
    subtitle: 'Add your first child to start the journey',
    nameLabel: 'Nickname',
    namePlaceholder: 'e.g. Sammy',
    ageRangeLabel: 'Age range',
    ageRangeHint: "We'll auto-import age-appropriate check-ins, habit rules, and store items",
    'ageRange_3-5': 'Ages 3-5',
    'ageRange_6-9': 'Ages 6-9',
    ageRange_10plus: 'Ages 10+',
    create: 'Create child',
    creating: 'Creating...',
    errors: {
      nameRequired: 'Please enter a nickname',
      createFailed: 'Failed to create. Please retry.',
    },
  },
  reports: {
    title: 'Family Report',
    empty: 'No data for this period',
    exportImage: 'Export image',
    preset: {
      thisWeek: 'This week',
      lastWeek: 'Last week',
      thisMonth: 'This month',
      lastMonth: 'Last month',
    },
    card: {
      totalPoints: 'Total',
      availablePoints: 'Available',
      gained: 'Gained',
      lost: 'Lost',
      net: 'Net',
      gainedShort: 'Add',
      lostShort: 'Sub',
      topBonus: 'TOP bonus',
      topDeduction: 'TOP deduction',
      checkinRate: 'Check-in rate',
      redemptions: 'Redemptions',
      noRedemptions: 'No redemptions',
    },
    toast: {
      fetchFailed: 'Failed to fetch report',
    },
  },
};

export default enUS;
